import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface Branch {
  id: string;
  code: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  status: string;
  created_at: string;
}

const emptyForm = { code: '', name: '', city: '', address: '', phone: '', status: 'active' };

export const BranchesPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);

  // Create flow: step 1 = form, step 2 = logo upload
  const [createStep, setCreateStep] = useState<0 | 1 | 2>(0);
  const [newBranchId, setNewBranchId] = useState('');
  const [newBranchName, setNewBranchName] = useState('');

  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user) navigate('/login');
    fetchBranches();
  }, [user]);

  const fetchBranches = async () => {
    try {
      const r = await axios.get(`${API}/branches`, { headers });
      setBranches(Array.isArray(r.data) ? r.data : (r.data.data || []));
    } catch {
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setFormData(emptyForm);
    setError('');
    setCreateStep(1);
    setEditBranch(null);
    setLogoFile(null);
    setLogoPreview('');
  };

  const openEdit = (b: Branch) => {
    setFormData({ code: b.code, name: b.name, city: b.city || '', address: b.address || '', phone: b.phone || '', status: b.status });
    setError('');
    setEditBranch(b);
    setCreateStep(0);
  };

  const closeAll = () => {
    setCreateStep(0);
    setEditBranch(null);
    setNewBranchId('');
    setLogoFile(null);
    setLogoPreview('');
    setError('');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const r = await axios.post(`${API}/branches`, formData, { headers });
      const created: Branch = r.data.data;
      setNewBranchId(created.id);
      setNewBranchName(created.name);
      setCreateStep(2);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal menyimpan cabang');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !newBranchId) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', logoFile);
      await axios.post(`${API}/branch-config/media/${newBranchId}/logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      closeAll();
      fetchBranches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSkipLogo = () => {
    closeAll();
    fetchBranches();
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editBranch) return;
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/branches/${editBranch.id}`, formData, { headers });
      setEditBranch(null);
      fetchBranches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal memperbarui cabang');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b: Branch) => {
    if (!window.confirm(`Hapus cabang "${b.name}"? Semua data terkait akan ikut terhapus.`)) return;
    try {
      await axios.delete(`${API}/branches/${b.id}`, { headers });
      fetchBranches();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Gagal menghapus cabang');
    }
  };

  if (loading) return <div style={s.loader}>Loading...</div>;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1>🏢 Manajemen Cabang</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
            {branches.length} cabang · Satu database terpusat, semua cabang terisolasi per branch_id
          </p>
        </div>
        <button onClick={openCreate} style={s.addBtn}>+ Tambah Cabang</button>
      </div>

      {/* === MODAL CREATE STEP 1: Info Cabang === */}
      {createStep === 1 && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.stepBar}>
              <div style={{ ...s.stepDot, background: '#667eea' }}>1</div>
              <div style={s.stepLine} />
              <div style={{ ...s.stepDot, background: '#ddd', color: '#999' }}>2</div>
            </div>
            <h2 style={s.modalTitle}>➕ Data Cabang Baru</h2>
            {error && <div style={s.errorBox}>{error}</div>}
            <form onSubmit={handleCreate} style={s.form}>
              <div style={s.field}>
                <label style={s.label}>Kode Cabang *</label>
                <input style={s.input} value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })} required placeholder="MNS-SOC" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Nama Cabang *</label>
                <input style={s.input} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required placeholder="Solo International Minisoccer" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Kota *</label>
                <input style={s.input} value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} required placeholder="Solo" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Alamat</label>
                <input style={s.input} value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Jl. ..." />
              </div>
              <div style={s.field}>
                <label style={s.label}>No. Telepon</label>
                <input style={s.input} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="08xx-xxxx-xxxx" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Status</label>
                <select style={s.input} value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Tidak Aktif</option>
                </select>
              </div>
              <div style={s.modalActions}>
                <button type="submit" disabled={saving} style={s.saveBtn}>{saving ? 'Menyimpan...' : 'Lanjut → Upload Logo'}</button>
                <button type="button" onClick={closeAll} style={s.cancelBtn}>Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* === MODAL CREATE STEP 2: Upload Logo === */}
      {createStep === 2 && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.stepBar}>
              <div style={{ ...s.stepDot, background: '#27ae60' }}>✓</div>
              <div style={{ ...s.stepLine, background: '#27ae60' }} />
              <div style={{ ...s.stepDot, background: '#667eea' }}>2</div>
            </div>
            <h2 style={s.modalTitle}>🖼️ Upload Logo — {newBranchName}</h2>
            <p style={{ color: '#666', fontSize: 13, margin: '0 0 20px' }}>
              Logo ini akan otomatis muncul di semua struk/resi cabang. Format: PNG/JPG, maks 5MB.
            </p>
            {error && <div style={s.errorBox}>{error}</div>}

            <div style={s.logoUploadArea} onClick={() => fileRef.current?.click()}>
              {logoPreview ? (
                <img src={logoPreview} alt="preview" style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ color: '#aaa', textAlign: 'center' }}>
                  <div style={{ fontSize: 36 }}>📷</div>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Klik untuk pilih file logo</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoFileChange} />
            </div>

            <div style={s.modalActions}>
              <button onClick={handleLogoUpload} disabled={!logoFile || logoUploading} style={s.saveBtn}>
                {logoUploading ? 'Mengupload...' : '✓ Upload & Selesai'}
              </button>
              <button onClick={handleSkipLogo} style={s.cancelBtn}>Lewati (upload nanti di Branch Config)</button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL EDIT === */}
      {editBranch && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>✏️ Edit Cabang — {editBranch.name}</h2>
            {error && <div style={s.errorBox}>{error}</div>}
            <form onSubmit={handleEdit} style={s.form}>
              <div style={s.field}>
                <label style={s.label}>Kode Cabang</label>
                <input style={{ ...s.input, background: '#f5f5f5', color: '#999' }} value={formData.code} disabled />
              </div>
              <div style={s.field}>
                <label style={s.label}>Nama Cabang *</label>
                <input style={s.input} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Kota *</label>
                <input style={s.input} value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Alamat</label>
                <input style={s.input} value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div style={s.field}>
                <label style={s.label}>No. Telepon</label>
                <input style={s.input} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Status</label>
                <select style={s.input} value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Tidak Aktif</option>
                </select>
              </div>
              <div style={s.modalActions}>
                <button type="submit" disabled={saving} style={s.saveBtn}>{saving ? 'Menyimpan...' : 'Simpan Perubahan'}</button>
                <button type="button" onClick={closeAll} style={s.cancelBtn}>Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branch Cards */}
      <div style={s.grid}>
        {branches.map(b => (
          <div key={b.id} style={s.card}>
            <div style={s.cardHead}>
              <div>
                <div style={s.branchName}>{b.name}</div>
                <div style={s.branchCode}>{b.code}</div>
              </div>
              <span style={{ ...s.badge, background: b.status === 'active' ? '#27ae60' : '#95a5a6' }}>
                {b.status === 'active' ? 'Aktif' : 'Nonaktif'}
              </span>
            </div>
            <div style={s.cardBody}>
              {b.city && <p style={s.info}>📍 {b.city}{b.address ? ` — ${b.address}` : ''}</p>}
              {b.phone && <p style={s.info}>📞 {b.phone}</p>}
            </div>
            <div style={s.cardFooter}>
              <button onClick={() => openEdit(b)} style={s.editBtn}>✏️ Edit</button>
              <button onClick={() => handleDelete(b)} style={s.deleteBtn}>🗑️</button>
            </div>
          </div>
        ))}
        {branches.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: '#999' }}>
            Belum ada cabang. Klik "Tambah Cabang" untuk memulai.
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  container: { padding: 20, background: '#f5f5f5', minHeight: '100vh' },
  header: { background: 'white', padding: 20, borderRadius: 8, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  addBtn: { padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 as const, fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 },
  card: { background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' },
  cardHead: { background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  branchName: { fontWeight: 700 as const, fontSize: 16 },
  branchCode: { fontSize: 12, opacity: 0.8, marginTop: 2 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 11, color: 'white', fontWeight: 600 as const },
  cardBody: { padding: '12px 20px' },
  info: { margin: '6px 0', fontSize: 13, color: '#555' },
  cardFooter: { display: 'flex', gap: 8, padding: '10px 20px', background: '#f9f9f9', borderTop: '1px solid #eee' },
  editBtn: { flex: 1, padding: '8px 0', background: '#667eea', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 },
  notifBtn: { flex: 1, padding: '8px 0', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 },
  deleteBtn: { padding: '8px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 },
  loader: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' },
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 10, padding: 30, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' as const, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  stepBar: { display: 'flex', alignItems: 'center', marginBottom: 24 },
  stepDot: { width: 32, height: 32, borderRadius: '50%', color: 'white', fontWeight: 700 as const, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
  stepLine: { flex: 1, height: 3, background: '#ddd', margin: '0 8px' },
  modalTitle: { margin: '0 0 20px 0', fontSize: 20, color: '#333' },
  form: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 },
  field: { display: 'flex', flexDirection: 'column' as const, gap: 6 },
  label: { fontSize: 13, fontWeight: 600 as const, color: '#444' },
  input: { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' as const },
  modalActions: { gridColumn: '1 / -1', display: 'flex', gap: 10, marginTop: 5, flexDirection: 'column' as const },
  saveBtn: { padding: '11px 0', background: '#667eea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 as const, fontSize: 14 },
  cancelBtn: { padding: '10px 0', background: '#f5f5f5', color: '#666', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  errorBox: { background: '#fee', color: '#c33', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13, gridColumn: '1 / -1' as const },
  logoUploadArea: { border: '2px dashed #ddd', borderRadius: 8, padding: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', minHeight: 160, marginBottom: 20, background: '#fafafa' },
};
